import { useCallback, useEffect, useRef, useState } from 'react';
import { GAME_STEPS, DEFAULT_GAME_STATE } from '../data/gameData.jsx';
import { playCorrect, playWrong, playFinale, playMerge, playTravel } from '../utils/sound.jsx';

const STORAGE_KEY = 'weddingGameState';

function loadInitialGameState() {
  try {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return saved ? JSON.parse(saved) : DEFAULT_GAME_STATE;
  } catch {
    return DEFAULT_GAME_STATE;
  }
}

// Single source of truth for the game flow: where each avatar is on the board,
// which modal is open, the merge/altar state machine, the celebration popup,
// and the small ephemeral UI signals (shake on wrong answer, the firework that
// pops over a freshly-cleared node, the toast that shows the "Our story" blurb).
//
// `dragStateRef` is passed in from useDragScroll so we can suppress clicks that
// happened as part of a drag gesture without re-routing it through React state.
export function useGameState({ dragStateRef } = {}) {
  const [gameState, setGameState] = useState(loadInitialGameState);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [activeStory, setActiveStory] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [shake, setShake] = useState(false);
  const [activeFirework, setActiveFirework] = useState(null);
  const [showFirework, setShowFirework] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  // After a correct answer we open the story reveal modal and stash the move
  // that should fire when the guest dismisses it. The avatar walk waits for
  // that close so the reveal isn't competing with the on-board motion.
  const [pendingAdvance, setPendingAdvance] = useState(null);

  // The avatars have reached the merge point on both paths.
  const isMerged =
    gameState.p1Step >= GAME_STEPS.p1.length && gameState.p2Step >= GAME_STEPS.p2.length;
  const isGameComplete = gameState.status === 'altar' || gameState.status === 'rsvp_done';

  // Re-celebrate only once on a fresh reach of the altar state, not on every
  // status change after that (e.g. when status flips to rsvp_done).
  const hasCelebrated = useRef(
    gameState.status === 'altar' || gameState.status === 'rsvp_done',
  );

  // Persist game progress.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(gameState));
  }, [gameState]);

  // Merge/fuse/merged state machine — chains 4 timeouts after both avatars reach
  // the merge point. The dep array is INTENTIONALLY just [isMerged]: the chain
  // itself mutates gameState.status and gameState.jointStep, so including those
  // in deps would cause the effect to re-run mid-sequence, the cleanup to clear
  // the still-pending timers, and the animation to freeze at "merging". The
  // functional setState form (`setGameState(prev => ...)`) means we don't need
  // the latest values in scope.
  useEffect(() => {
    if (!isMerged) return;
    let t1, t2, t3, t4;
    // Only kick off the chain the first time we observe isMerged becoming true
    // with the game still in its pre-merge state.
    if (gameState.jointStep === -1 && gameState.status === 'playing') {
      t1 = setTimeout(() => {
        playMerge();
        setGameState((prev) => ({ ...prev, status: 'merging', jointStep: 0 }));
        t3 = setTimeout(() => {
          setGameState((prev) => ({ ...prev, status: 'fusing' }));
        }, 6000);
        t2 = setTimeout(() => {
          setGameState((prev) => ({ ...prev, status: 'merged' }));
          setShowFirework(true);
        }, 7000);
        t4 = setTimeout(() => setShowFirework(false), 10000);
      }, 1200);
    }
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMerged]);

  // Pop the celebration overlay once when the altar is reached.
  useEffect(() => {
    if (gameState.status === 'altar' && !hasCelebrated.current) {
      hasCelebrated.current = true;
      playFinale();
      setShowCelebration(true);
      const timer = setTimeout(() => setShowCelebration(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [gameState.status]);

  const handleMistake = useCallback(() => {
    playWrong();
    setGameState((prev) => ({ ...prev, mistakes: prev.mistakes + 1 }));
    setShake(true);
    setTimeout(() => setShake(false), 500);
  }, []);

  // Open the modal for a node — only if the node is the current step and we
  // didn't just finish a drag.
  const handleNodeClick = useCallback(
    (player, index) => {
      if (dragStateRef && dragStateRef.current && dragStateRef.current.isPreventClick) return;
      if (index < 0 || index >= GAME_STEPS[player].length) return;

      setToastMessage(null);

      const opens =
        (player === 'p1' && index === gameState.p1Step) ||
        (player === 'p2' && index === gameState.p2Step) ||
        (player === 'joint' && index === gameState.jointStep);
      if (opens) {
        setActiveQuestion({ player, index });
      }
    },
    [dragStateRef, gameState.p1Step, gameState.p2Step, gameState.jointStep],
  );

  // Opening a planted flag's story recap — wrapped so it can carry its own
  // gentle audio cue.
  const openStory = useCallback((step) => {
    playStory();
    setActiveStory(step);
  }, []);

  const handleAnswer = useCallback(
    (answer) => {
      const { player, index } = activeQuestion;
      const correct = GAME_STEPS[player][index].answer;

      if (answer === correct || answer === 'Yes!' || answer === 'PuzzleSolved') {
        // The last joint answer flips status to 'altar', which fires the
        // triumphant win cue from the celebration effect — don't double it up
        // with the per-step "correct" chime here.
        const finishesJourney =
          player === 'joint' && index + 1 >= GAME_STEPS.joint.length;
        if (!finishesJourney && answer !== 'PuzzleSolved') playCorrect();
        setActiveFirework({ cx: GAME_STEPS[player][index].cx, cy: GAME_STEPS[player][index].cy });
        setTimeout(() => setActiveFirework(null), 2500);

        // Close the question, reveal the answer in the story modal, and stash
        // the advance until the guest dismisses it (see closeStory).
        setActiveQuestion(null);
        setActiveStory(GAME_STEPS[player][index]);
        setPendingAdvance({ player, finishesJourney });
      } else {
        handleMistake();
      }
    },
    [activeQuestion, handleMistake],
  );

  // Story modal close handler. When closing a post-answer reveal, this is also
  // the moment the avatar starts walking to its new node, so we play the travel
  // cue and advance state here instead of in handleAnswer.
  const closeStory = useCallback(() => {
    setActiveStory(null);
    if (!pendingAdvance) return;
    const { player, finishesJourney } = pendingAdvance;
    if (!finishesJourney) playTravel(player === 'joint' ? 0.1 : 1.2);
    setGameState((prev) => {
      const nextState = { ...prev };
      if (player === 'p1') nextState.p1Step += 1;
      if (player === 'p2') nextState.p2Step += 1;
      if (player === 'joint') {
        nextState.jointStep += 1;
        if (nextState.jointStep >= GAME_STEPS.joint.length) {
          nextState.status = 'altar';
        }
      }
      return nextState;
    });
    setPendingAdvance(null);
  }, [pendingAdvance]);

  // Curried, stable per-player click handlers so memo'd children don't churn.
  const handleP1Click = useCallback((i) => handleNodeClick('p1', i), [handleNodeClick]);
  const handleP2Click = useCallback((i) => handleNodeClick('p2', i), [handleNodeClick]);
  const handleJointClick = useCallback((i) => handleNodeClick('joint', i), [handleNodeClick]);
  const handleAltarClick = useCallback(() => handleNodeClick('joint', 4), [handleNodeClick]);
  const handleAvatarP1Click = useCallback(
    () => handleNodeClick('p1', gameState.p1Step),
    [handleNodeClick, gameState.p1Step],
  );
  const handleAvatarP2Click = useCallback(
    () => handleNodeClick('p2', gameState.p2Step),
    [handleNodeClick, gameState.p2Step],
  );
  const handleJointAvatarClick = useCallback(
    () => handleNodeClick('joint', gameState.jointStep),
    [handleNodeClick, gameState.jointStep],
  );

  const resetGame = useCallback(() => setGameState(DEFAULT_GAME_STATE), []);
  const goToAltar = useCallback(() => setGameState((prev) => ({ ...prev, status: 'altar' })), []);
  const markRsvpDone = useCallback(
    () => setGameState((prev) => ({ ...prev, status: 'rsvp_done' })),
    [],
  );

  return {
    gameState,
    setGameState,
    isMerged,
    isGameComplete,

    activeQuestion,
    setActiveQuestion,
    activeStory,
    setActiveStory,
    closeStory,
    openStory: setActiveStory,
    toastMessage,
    shake,
    activeFirework,
    showFirework,
    showCelebration,
    setShowCelebration,

    handleAnswer,
    handleMistake,
    handleP1Click,
    handleP2Click,
    handleJointClick,
    handleAltarClick,
    handleAvatarP1Click,
    handleAvatarP2Click,
    handleJointAvatarClick,
    resetGame,
    goToAltar,
    markRsvpDone,
  };
}
